// ---- World items (the instances lying in the world) ----
// Client-side mirror of the server's authoritative item list, kept in sync by net.ts
// under the interest-management contract: items ENTER via `state.items` and are removed
// ONLY via the `leave.items` list (absence from a snapshot does NOT mean gone — see
// IM_DEFAULTS in shared.ts). Each live item is a buildItemMesh() Group added to the
// scene at its world spot. This module owns the meshes; net.ts owns the wire. The click
// handler (game.ts) and the hover FX (updateWorldItems) both raycast worldItemMeshes().
//
// Reuses the raycaster idiom from focus.ts. Items rest still; the one under the
// cursor bobs + slowly spins + scales up a touch so it reads as pick-up-able.
// Canvas pointer cursor is owned by src/cursor.ts — we only report hover via
// registerHoverProbe (do not write canvas.style.cursor here).

import * as THREE from "three";
import { buildItemMesh, resolveItemType } from "./index.ts";
import type { WorldItem } from "../shared.ts";
import { hideOutdoorWhileIndoor } from "../cutaway.ts";
import { registerHoverProbe, worldPointerNdc } from "../cursor.ts";

type Live = { item: WorldItem; mesh: THREE.Group };

const live = new Map<string, Live>();
let scene: THREE.Scene;

const raycaster = new THREE.Raycaster();
const ndc = new THREE.Vector2();

export function initWorldItems(s: THREE.Scene, _c: HTMLCanvasElement): void {
  scene = s;
  // Cursor probe uses the precomputed `hovered` from updateWorldItems (same
  // frame order: updateWorldItems → updateWorldCursor).
  registerHoverProbe(() => hovered !== null);
}

function spawn(item: WorldItem): void {
  // Stale persistence / pre-migration renames (e.g. lance→spear) — never crash
  // the whole client over one ground item. Server remaps on restore; this is
  // the client belt for a mid-session content skew.
  const type = resolveItemType(item.type);
  if (!type) {
    console.warn(`[world-items] skip unknown type "${item.type}" id=${item.id}`);
    return;
  }
  const mesh = buildItemMesh(type);
  mesh.position.set(item.x, item.y, item.z);
  scene.add(mesh);
  live.set(item.id, { item: { ...item, type }, mesh });
}

function despawn(id: string): void {
  const l = live.get(id);
  if (!l) return;
  scene.remove(l.mesh);
  live.delete(id);
}

/** Replace the whole set from a server snapshot (sent once on join). */
/** Reset the whole mirror to `items` (drops every current mesh first). Used on the
 *  reconnect resync — NOT the steady-state path, which is addWorldItem/removeWorldItem
 *  driven by enter/leave. */
export function setWorldItems(items: WorldItem[]): void {
  for (const id of [...live.keys()]) despawn(id);
  for (const item of items) spawn(item);
}

export function addWorldItem(item: WorldItem): void {
  if (live.has(item.id)) return;
  spawn(item);
}

/** Remove an item by id and return the type it was (so the picker can pocket it). */
export function removeWorldItem(id: string): ItemType | null {
  const l = live.get(id);
  if (!l) return null;
  const type = l.item.type;
  despawn(id);
  return type;
}

/** An item's ground position (xz) by id, or null if it isn't live. Used by the
 *  pickup click to gate on proximity before sending. */
export function worldItemPos(id: string): { x: number; z: number } | null {
  const l = live.get(id);
  return l ? { x: l.item.x, z: l.item.z } : null;
}

/** An item's type by id, or null if it isn't live. Lets the pickup gate check
 *  stack room for the specific type (a full bag can still top up an arrow stack). */
export function worldItemType(id: string): ItemType | null {
  return live.get(id)?.item.type ?? null;
}

/** Live item meshes for raycasting (click pickup + hover FX). */
export function worldItemMeshes(): { id: string; type: ItemType; mesh: THREE.Group }[] {
  const out: { id: string; type: ItemType; mesh: THREE.Group }[] = [];
  for (const l of live.values()) out.push({ id: l.item.id, type: l.item.type, mesh: l.mesh });
  return out;
}

/** Raycast the pointer against live items; nearest hit's id, or null. */
export function pickItemAt(camera: THREE.Camera, clientX: number, clientY: number): string | null {
  ndc.x = (clientX / window.innerWidth) * 2 - 1;
  ndc.y = -(clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(ndc, camera);
  let hitId: string | null = null;
  let best = Infinity;
  for (const l of live.values()) {
    if (hideOutdoorWhileIndoor(l.item.x, l.item.z)) continue;
    const inter = raycaster.intersectObject(l.mesh, true);
    if (inter.length && inter[0]!.distance < best) {
      best = inter[0]!.distance;
      hitId = l.item.id;
    }
  }
  return hitId;
}

// Per-frame hover FX. Raycast the current pointer; the hovered item bobs + spins
// + scales up; everything else eases back to its resting pose. `hovered` also
// drives the canvas pointer cursor (src/cursor.ts probe).
let hovered: string | null = null;

export function updateWorldItems(camera: THREE.Camera, dt: number, time: number): void {
  hovered = null;
  const ptr = worldPointerNdc();
  if (ptr) {
    ndc.copy(ptr);
    raycaster.setFromCamera(ndc, camera);
    let best = Infinity;
    const co = camera.position;
    for (const l of live.values()) {
      // Hover FX only matters for items the player can SEE — cheap distance
      // check before the (much pricier) ray-vs-mesh test, so a loot-strewn map
      // isn't ray-testing every live drop every frame. Cut at the fog-opaque
      // line (fog runs 60→135 — daynight.ts), not pickup range: a drop on a
      // distant hillside is plainly visible and should still light up.
      if (hideOutdoorWhileIndoor(l.item.x, l.item.z)) continue;
      const dx = l.item.x - co.x, dz = l.item.z - co.z;
      if (dx * dx + dz * dz > 135 * 135) continue; // beyond full fog — invisible
      const inter = raycaster.intersectObject(l.mesh, true);
      if (inter.length && inter[0]!.distance < best) {
        best = inter[0]!.distance;
        hovered = l.item.id;
      }
    }
  }

  for (const l of live.values()) {
    const m = l.mesh;
    // Outdoor ground items vanish while the local player is indoors (blackout);
    // wire interest is unchanged — this is presentation-only.
    const outdoorHide = hideOutdoorWhileIndoor(l.item.x, l.item.z);
    if (m.visible === outdoorHide) m.visible = !outdoorHide;
    if (outdoorHide) continue;

    const on = l.item.id === hovered;
    // Scale eases toward 1.15 when hovered, back to 1 otherwise.
    const wantScale = on ? 1.15 : 1;
    const s = m.scale.x + (wantScale - m.scale.x) * Math.min(1, dt * 10);
    m.scale.set(s, s, s);
    if (on) {
      m.position.y = l.item.y + Math.sin(time * 3) * 0.08; // gentle bob
      m.rotation.y += dt * 1.6;                            // slow spin
    } else {
      m.position.y += (l.item.y - m.position.y) * Math.min(1, dt * 10);
    }
  }
}
